// src/pages/Masters/MapAccoundCode.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button, Modal, Row, Col, Form, OverlayTrigger } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { MdEdit, MdDelete, MdFilterList, MdAddCircle } from "react-icons/md";
import { ToastContainer, toast } from "react-toastify";
import Tooltip from "@mui/material/Tooltip";
import AppDataGrid from "../../../components/common/AppDataGrid";
import loading from "../../../assets/images/common/loading.gif";
import { fetchDataRead } from "../../../Utility";

const MapAccoundCode = () => {
  const [showFilterModal, setShowFilterModal] = useState(false);
  const handleCloseFilterModal = () => setShowFilterModal(false);
  const handleShowFilterModal = () => setShowFilterModal(true);

  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};
  const readOnlyRoles = ["Class Teacher", "Teacher", "Class Incharge", "School Admin"];
  const canSubmit = !readOnlyRoles.includes(userObj.role_name?.trim());

  // ---------- Filter State ----------
  // pendingFilter = what user is typing in modal
  // activeFilter  = what is actually applied to the grid
  const [pendingFilter, setPendingFilter] = useState({
    hsn_sac: "",
    code: "",
    gst_type: "",
    effective_date: "",
  });

  const [activeFilter, setActiveFilter] = useState({
    hsn_sac: "",
    code: "",
    gst_type: "",
    effective_date: "",
  });

  // ---------- Actions ----------
  const handleEditClick = (id) => {
    const rec = rows.find((r) => r.account_code_mapping_id === id);
    if (rec) navigate("/finance/gst/mapping/add", { state: { mapData: rec } });
  };

  const handleDeleteClick = async (id) => {
    if (!canSubmit) return;

    const yes = window.confirm("Are you sure you want to change the status?");
    if (!yes) return;

    try {
      const payload = {
        account_code_mapping_id: id,
        modified_by:
          userObj.user_name ||
          userObj.username ||
          userObj.email ||
          userObj.email_id ||
          "system",
      };

      const resp = await axios.post(
        `${baseUrl}/inactive-account-code-mapping/`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );

      if (resp.status < 200 || resp.status >= 300) {
        throw new Error(`HTTP error! Status: ${resp.status}`);
      }

      const msg = resp.data?.message || "Status set to Inactive";
      toast.success(msg);

      // Refresh grid
      await fetchDataRead("/account-code-mapping/search/", setRows, userObj.school_id);
    } catch (err) {
      console.error(err);
      toast.error("Failed to update status");
    }
  };

  // ---------- Columns ----------
  const baseColumns = [
    {
      field: "hsn_sac_code",
      headerName: "HSN/ SAC",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value || ""}>
          <span className="cell-wrap">{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "code_type_name",
      headerName: "Code",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value || ""}>
          <span className="cell-wrap">{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "gst_type_name",
      headerName: "GST Type",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value || ""}>
          <span className="cell-wrap">{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "effective_date",
      headerName: "Effective Date",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (params) => {
        if (!params.value) return "";

        const d = new Date(params.value);
        const value = `${String(d.getDate()).padStart(2, "0")}-${String(
          d.getMonth() + 1
        ).padStart(2, "0")}-${d.getFullYear()}`;

        return (
          <Tooltip title={value}>
            <span className="cell-wrap">{value}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "is_active",
      headerName: "Status",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (params) => {
        const raw = params.value;
        let label = "";

        if (raw === true) {
          label = "Active";
        } else if (raw === false) {
          label = "InActive";
        } else {
          label = "";
        }

        return (
          <Tooltip title={label}>
            <span className="cell-wrap">{label}</span>
          </Tooltip>
        );
      },
    },
  ];

  const actionColumn = {
    field: "actions",
    headerName: "Actions",
    headerAlign: "center",
    align: "center",
    sortable: false,
    flex: 0.6,
    renderCell: (params) =>
      params.row.account_code_mapping_id !== "No records found" ? (
        <div className="tableActions">
          <Tooltip title="Edit" arrow>
            <span
              className="commonActionIcons"
              onClick={() => handleEditClick(params.row.account_code_mapping_id)}
            >
              <MdEdit />
            </span>
          </Tooltip>
          <Tooltip title="Delete" arrow>
            <span
              className="commonActionIcons"
              onClick={() => handleDeleteClick(params.row.account_code_mapping_id)}
            >
              <MdDelete />
            </span>
          </Tooltip>
        </div>
      ) : null,
  };

  const columns = canSubmit ? [...baseColumns, actionColumn] : [...baseColumns];

  // ---------- Data fetch ----------
  useEffect(() => {
    setIsLoading(true);
    fetchDataRead("/account-code-mapping/search/", setRows, userObj.school_id).finally(() =>
      setIsLoading(false)
    );
  }, []); // eslint-disable-line

  // ---------- Search + Filter ----------
  const searchableColumns = [
    (r) => r.hsn_sac_code,
    (r) => r.code_type_name,
    (r) => r.gst_type_name,
    (r) => r.effective_date,
  ];

  const filteredRecords = (rows || []).filter((r) => {
    // Top text search
    const matchesSearch = searchableColumns.some((sel) =>
      String(sel(r) || "").toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Modal filters – use activeFilter (only applied after Search button)
    const matchesFilterHSN =
      !activeFilter.hsn_sac ||
      String(r.hsn_sac_code || "")
        .toLowerCase()
        .includes(activeFilter.hsn_sac.toLowerCase());

    const matchesFilterCode =
      !activeFilter.code ||
      String(r.code_type_name || "")
        .toLowerCase()
        .includes(activeFilter.code.toLowerCase());

    const matchesFilterGstType =
      !activeFilter.gst_type ||
      String(r.gst_type_name || "")
        .toLowerCase()
        .includes(activeFilter.gst_type.toLowerCase());

    const matchesFilterDate =
      !activeFilter.effective_date ||
      (r.effective_date &&
        String(r.effective_date).startsWith(activeFilter.effective_date)); // YYYY-MM-DD

    return (
      matchesSearch &&
      matchesFilterHSN &&
      matchesFilterCode &&
      matchesFilterGstType &&
      matchesFilterDate
    );
  });

  const handleSearchChange = (e) => {
    // Just update query; grid uses filteredRecords
    setSearchQuery(e.target.value);
  };

  // ---------- Filter Modal Handlers ----------
  const handleFilterSubmit = async (e) => {
    e.preventDefault();
    // Apply what user typed in modal
    setActiveFilter(pendingFilter);
    setShowFilterModal(false);
  };

  const handleFilterClear = async () => {
    setPendingFilter({
      hsn_sac: "",
      code: "",
      gst_type: "",
      effective_date: "",
    });

    setActiveFilter({
      hsn_sac: "",
      code: "",
      gst_type: "",
      effective_date: "",
    });

    setSearchQuery("");
    setIsLoading(true);
    try {
      await fetchDataRead("/account-code-mapping/search/", setRows, userObj.school_id);
    } catch (err) {
      console.error("Error fetching all records:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="pageBody">
      <ToastContainer />

      <div className="commonDataTableHead">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="d-flex align-items-center" style={{ gap: "10px" }}>
            <h6 className="commonTableTitle">Map Account Code</h6>
          </div>

          <div>
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              className="searchInput"
              onChange={handleSearchChange}
            />
          </div>

          <div className="d-flex align-items-center" style={{ gap: 6 }}>
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="tooltip-top">Filter</Tooltip>}
            >
              <Button className="secondaryBtn" variant="secondary" onClick={handleShowFilterModal}>
                <MdFilterList />
              </Button>
            </OverlayTrigger>

            {canSubmit && (
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="tooltip-top">Add</Tooltip>}
              >
                <Button
                  className="primaryBtn"
                  variant="primary"
                  onClick={() => navigate("/finance/gst/mapping/add")}
                >
                  <MdAddCircle />
                </Button>
              </OverlayTrigger>
            )}
          </div>
        </div>
      </div>

      <div className="commonTable height100">
        <div className="tableBody">
          {isLoading ? (
            <div className="loadingContainer">
              <img src={loading} alt="Loading..." className="loadingGif" />
            </div>
          ) : (
            <AppDataGrid
              rows={filteredRecords}
              columns={columns}
              getRowId={(r) => r.account_code_mapping_id}
              showToolbar={false}
              pagination
              pageSizeOptions={[10, 20, 50]}
              density="compact"
              maxHeight={500}
            />
          )}
        </div>
      </div>

      {/* Filter Modal */}
      <Modal show={showFilterModal} onHide={handleCloseFilterModal} className="commonFilterModal">
        <Modal.Header closeButton className="modalHeaderFixed">
          <Modal.Title>Filter</Modal.Title>
        </Modal.Header>

        <Modal.Body className="modalBodyScrollable">
          <Form id="filterForm" onSubmit={handleFilterSubmit}>
            <Row>
              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group controlId="hsnsac">
                    <Form.Label>HSN/ SAC</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter HSN/SAC"
                      value={pendingFilter.hsn_sac}
                      onChange={(e) =>
                        setPendingFilter((p) => ({ ...p, hsn_sac: e.target.value }))
                      }
                    />
                  </Form.Group>
                </div>
              </Col>

              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group controlId="code">
                    <Form.Label>Code</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter code"
                      value={pendingFilter.code}
                      onChange={(e) =>
                        setPendingFilter((p) => ({ ...p, code: e.target.value }))
                      }
                    />
                  </Form.Group>
                </div>
              </Col>

              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group controlId="gstType">
                    <Form.Label>GST Type</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter GST Type"
                      value={pendingFilter.gst_type}
                      onChange={(e) =>
                        setPendingFilter((p) => ({ ...p, gst_type: e.target.value }))
                      }
                    />
                  </Form.Group>
                </div>
              </Col>

              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group controlId="effectiveDate">
                    <Form.Label>Effective Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={pendingFilter.effective_date}
                      onChange={(e) =>
                        setPendingFilter((p) => ({ ...p, effective_date: e.target.value }))
                      }
                    />
                  </Form.Group>
                </div>
              </Col>
            </Row>
          </Form>
        </Modal.Body>

        <Modal.Footer className="modalFooterFixed">
          <div>
            <Button
              variant="secondary"
              className="btn-info clearBtn"
              onClick={handleFilterClear}
            >
              Reset
            </Button>
          </div>
          <div>
            <Button
              variant="secondary"
              className="btn-danger secondaryBtn"
              onClick={handleCloseFilterModal}
            >
              Close
            </Button>
            <Button
              variant="primary"
              className="btn-success primaryBtn"
              type="submit"
              form="filterForm"
            >
              Search
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MapAccoundCode;
